import { Button, Modal, Title } from "@mantine/core";
import * as Sentry from "@sentry/browser";
import { useEffect, useState } from "react";
import api from "../../../../api";
import { useEnergyDataAcquisitions } from "../../../../hooks/useEnergyDataAcquisitions";
import { ModalFooter } from "../../../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../../../mantine/notifications/useToast";
import urls from "../../../../urls";
import { LoadOrError } from "../../../LoadOrError/LoadOrError";
import type { AcquisitionWithSummaryData } from "../EnergyDataAcquisitionList/EnergyDataAcquisitionList";
import type { EnergyDataAcquisitionFilterOptions } from "../EnergyDataPage/Acquisitions/useEnergyDataAcquisitionFilterOptions";
import type { EnergyDataAcquisitionFilters } from "../EnergyDataPage/Acquisitions/useEnergyDataAcquisitionFilters";
import type { AcquisitionFilter } from "../EnergyDataView";
import { AcquisitionList } from "./AcquisitionList/AcquisitionList";
import { DefinedFilterDropdown } from "./DefinedFilterDropdown/DefinedFilterDropdown";
import "./EnergyDataAcquisitionSelectionModal.scss";
import { applySitePreselection } from "./Filters/applySitePreselection";
import { buildFilterString, EMPTY_FILTERS } from "./Filters/filter-utils";
import { Filters } from "./Filters/Filters";

export const DUMMY_DEFINED_FILTER_ID_FOR_NEW_FILTERS = -1;
const TEMP_DEFINED_FILTER_ID = -2;

export interface EdaFilter {
  appliedFilters: string;
  availableInAllProjects: boolean;
  filterName: string;
  id: number;
  project: string;
}

export interface EdaFilterOption {
  label: string;
  option: string;
  site: string;
  value: string | number | { min: number; max: number };
}

export type EdaFilterOptions = Array<EdaFilterOption>;

interface EnergyDataAcquisitionModalProps {
  isOpen: boolean;
  variantId: number;
  projectId: string;
  filterOptions: Record<string, EdaFilterOptions>; // string = FILTER_TYPES without installedCapacity and date
  onToggle: () => void;
  initialSelectedAcquisitions: Array<number>;
  initialAcquisitionFilters: Array<AcquisitionFilter>;
  initialDefinedFilterId: number | null;
  initialDefinedFilters: Array<EdaFilter>;
  onSelectedAcquisitionsChanged: (
    selectedAcquisitions: Array<number>,
    filters: Array<AcquisitionFilter>,
    definedFilterId: number | null
  ) => void;
}

function EnergyDataAcquisitionSelectionModal({
  isOpen,
  variantId,
  projectId,
  filterOptions,
  onToggle,
  initialSelectedAcquisitions,
  initialAcquisitionFilters,
  initialDefinedFilterId,
  initialDefinedFilters,
  onSelectedAcquisitionsChanged
}: EnergyDataAcquisitionModalProps) {
  const { showErrorToast } = useToast();

  const [tableSelection, setTableSelection] = useState<
    Array<AcquisitionWithSummaryData["id"]>
  >(initialSelectedAcquisitions);
  const [firstLoadHappened, setFirstLoadHappened] = useState(false);
  const [filters, setFilters] = useState(initialAcquisitionFilters);
  const [definedFilterId, setDefinedFilterId] = useState(
    initialDefinedFilterId
  );
  const [definedFilters, setDefinedFilters] = useState(
    initialDefinedFilters.map((definedFilter) => {
      let appliedFilters;

      try {
        appliedFilters = JSON.parse(definedFilter.appliedFilters);
      } catch (_) {
        Sentry.captureMessage(
          `Invalid filter sent to frontend: ${definedFilter.appliedFilters}`
        );
      }

      return {
        ...definedFilter,
        appliedFilters: appliedFilters ?? EMPTY_FILTERS
      };
    })
  );
  const [savingDefinedFilters, setSavingDefinedFilters] = useState(false);
  const filterString = buildFilterString(filters);
  const {
    data: acquisitions,
    isLoading: isLoadingAcquisitions,
    error: errorAcquisitions
  } = useEnergyDataAcquisitions(variantId, filterString);

  useEffect(() => {
    if (!acquisitions) {
      return;
    }

    setTableSelection((oldTableSelection) => {
      const newTableSelection = [
        ...acquisitions.map((acquisition) => acquisition.id)
      ];

      if (oldTableSelection.length > 0 || firstLoadHappened) {
        const filteredTableSelection = oldTableSelection.reduce(
          (filteredIds: Array<number>, selectedAcquisitionId) => {
            if (newTableSelection.includes(selectedAcquisitionId)) {
              filteredIds.push(selectedAcquisitionId);
            }

            return filteredIds;
          },
          []
        );

        return filteredTableSelection;
      } else {
        return newTableSelection;
      }
    });

    if (!firstLoadHappened) {
      setFirstLoadHappened(true);
    }
  }, [acquisitions, firstLoadHappened]);

  function handleChangeDefinedFilterId(filterId: number) {
    const filter = definedFilters.find(
      (definedFilter) => definedFilter.id === filterId
    );

    if (
      filterId === DUMMY_DEFINED_FILTER_ID_FOR_NEW_FILTERS ||
      (filter && filter.appliedFilters)
    ) {
      setDefinedFilterId(filterId);
    } else {
      setDefinedFilterId(null);
    }

    if (filter && filter.appliedFilters) {
      setFilters(filter.appliedFilters);
    }
  }

  function handleSaveDefinedFilter(definedFilterName: string) {
    const tempDefinedFilter = {
      appliedFilters: filters,
      availableInAllProjects: false,
      filterName: definedFilterName,
      id: TEMP_DEFINED_FILTER_ID,
      project: projectId
    };
    const oldDefinedFilters = [...definedFilters];
    const tempDefinedFilters = [...definedFilters];

    tempDefinedFilters.push(tempDefinedFilter);

    setDefinedFilters(tempDefinedFilters);
    setDefinedFilterId(TEMP_DEFINED_FILTER_ID);
    setSavingDefinedFilters(true);

    const newDefinedFilters = oldDefinedFilters;
    let newDefinedFilterId;

    api
      .post(urls.api.energyDataAcquisitionFilters(), {
        project: projectId,
        filterName: definedFilterName,
        appliedFilters: JSON.stringify(filters)
      })
      .then((response) => {
        newDefinedFilterId = response.data.id;
        const newDefinedFilter = {
          ...tempDefinedFilter,
          id: newDefinedFilterId
        };

        newDefinedFilters.push(newDefinedFilter);
      })
      .catch((error) => {
        newDefinedFilterId = DUMMY_DEFINED_FILTER_ID_FOR_NEW_FILTERS;
        showErrorToast(error);
      })
      .finally(() => {
        setDefinedFilters(newDefinedFilters);
        setDefinedFilterId(newDefinedFilterId);
        setSavingDefinedFilters(false);
      });
  }

  async function handleDeleteDefinedFilter(definedFilterId: number) {
    const oldDefinedFilters = [...definedFilters];
    const oldDefinedFilter = definedFilters.find(
      (definedFilter) => definedFilter.id === definedFilterId
    );
    const newDefinedFilters = definedFilters.filter(
      (definedFilter) => definedFilter.id !== definedFilterId
    );

    setFilters(EMPTY_FILTERS);
    setDefinedFilterId(DUMMY_DEFINED_FILTER_ID_FOR_NEW_FILTERS);
    setDefinedFilters(newDefinedFilters);

    try {
      await api.delete(
        urls.api.energyDataAcquisitionFiltersDetail(definedFilterId)
      );
    } catch (error) {
      showErrorToast(error);
      setDefinedFilters(oldDefinedFilters);
      setDefinedFilterId(definedFilterId);

      if (oldDefinedFilter) {
        setFilters(oldDefinedFilter.appliedFilters);
      }
    }
  }

  function handleChangeFilters(filters: AcquisitionFilter[]) {
    setFilters(filters);
    setDefinedFilterId(DUMMY_DEFINED_FILTER_ID_FOR_NEW_FILTERS);
  }

  function handleClickSave() {
    onToggle();
    onSelectedAcquisitionsChanged(tableSelection, filters, definedFilterId);
  }

  // note: the backend does not guarantee that filters are unique for some reason
  // therefore, we filter out duplicated filters here
  const uniqueDefinedFilters = definedFilters.reduce<
    Array<EdaFilter & { appliedFilters: string }>
  >((filters, filter) => {
    if (!filters.find((f) => f.id === filter.id)) {
      filters.push(filter);
    }

    return filters;
  }, []);

  return (
    <Modal
      className="EnergyDataAcquisitionSelectionModal"
      opened={isOpen}
      size="min(80vw, 1400px)"
      title="Energiedaten auswählen"
      onClose={onToggle}
    >
      <Title mb={15} order={4}>
        Filter
      </Title>
      <DefinedFilterDropdown
        definedFilterId={definedFilterId}
        definedFilters={uniqueDefinedFilters}
        savingDefinedFilters={savingDefinedFilters}
        onChange={handleChangeDefinedFilterId}
      />
      <Filters
        definedFilterId={definedFilterId}
        definedFilters={uniqueDefinedFilters}
        filterOptions={filterOptions}
        filters={filters}
        savingDefinedFilters={savingDefinedFilters}
        onChangeFilters={handleChangeFilters}
        onDeleteDefinedFilter={handleDeleteDefinedFilter}
        onSaveDefinedFilter={handleSaveDefinedFilter}
      />
      <Title mb={15} order={4}>
        Energiedaten
      </Title>
      <LoadOrError error={errorAcquisitions} loading={isLoadingAcquisitions}>
        {acquisitions && (
          <AcquisitionList
            acquisitions={acquisitions}
            selection={tableSelection}
            onSelect={setTableSelection}
          />
        )}
      </LoadOrError>
      <ModalFooter>
        <Button variant="default" onClick={onToggle}>
          Abbrechen
        </Button>
        <Button
          disabled={tableSelection.length === 0}
          onClick={handleClickSave}
        >
          Übernehmen
        </Button>
      </ModalFooter>
    </Modal>
  );
}

interface EnergyDataAcquisitionSelectionModalLoadedDataWrapperProps extends Omit<
  EnergyDataAcquisitionModalProps,
  "filterOptions" | "initialDefinedFilters"
> {
  data: {
    filterOptions: EnergyDataAcquisitionFilterOptions;
    definedFilters: EnergyDataAcquisitionFilters;
  };
}

function EnergyDataAcquisitionSelectionModalLoadedDataWrapper({
  data,
  isOpen,
  projectId,
  variantId,
  initialDefinedFilterId,
  initialSelectedAcquisitions,
  initialAcquisitionFilters,
  onSelectedAcquisitionsChanged,
  onToggle
}: EnergyDataAcquisitionSelectionModalLoadedDataWrapperProps) {
  const definedFilters = data.definedFilters;

  const validInitialDefinedFilterId = definedFilters.find(
    (definedFilter) => definedFilter.id === initialDefinedFilterId
  )
    ? initialDefinedFilterId
    : null;

  return (
    <EnergyDataAcquisitionSelectionModal
      filterOptions={data.filterOptions}
      initialAcquisitionFilters={applySitePreselection(
        initialAcquisitionFilters,
        data.filterOptions
      )}
      initialDefinedFilterId={validInitialDefinedFilterId}
      initialDefinedFilters={definedFilters}
      initialSelectedAcquisitions={initialSelectedAcquisitions}
      isOpen={isOpen}
      projectId={projectId}
      variantId={variantId}
      onSelectedAcquisitionsChanged={onSelectedAcquisitionsChanged}
      onToggle={onToggle}
    />
  );
}

export { EnergyDataAcquisitionSelectionModalLoadedDataWrapper as EnergyDataAcquisitionSelectionModal };
